import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import websocketService from '../services/websocketService';
import SessionConflictListener from '../components/session/SessionConflictListener';
import QueueRealtimeSyncListener from '../components/queue/QueueRealtimeSyncListener';

const WebSocketContext = createContext(undefined);

/**
 * WebSocketContext Provider
 * Manages persistent WebSocket connection for agents
 * Connection persists from login to logout (notifications, queue realtime, session security)
 */
export const WebSocketProvider = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');

  /**
   * Initialize persistent WebSocket connection for the agent notification channel.
   */
  const connect = useCallback(() => {
    if (websocketService.isAuthFailureTerminal()) {
      setIsConnected(false);
      setConnectionStatus('auth-failed');
      return;
    }

    // Check if already connected
    if (websocketService.getIsConnected()) {
      setIsConnected(true);
      setConnectionStatus('connected');
      return;
    }

    const token = sessionStorage.getItem('token') || sessionStorage.getItem('authToken');
    if (!token) {
      console.warn('No token available for WebSocket connection');
      setConnectionStatus('error');
      return;
    }

    setConnectionStatus('connecting');

    websocketService.connect(
      token,
      () => {
        setIsConnected(true);
        setConnectionStatus('connected');
        // disconnect() clears message handlers; let listeners (e.g. session conflict) re-subscribe
        window.dispatchEvent(new CustomEvent('websocket-connected'));
      },
      (error) => {
        console.error('WebSocket connection error:', error);
        setIsConnected(false);
        if (websocketService.isAuthFailureTerminal()) {
          setConnectionStatus('auth-failed');
        } else if (!websocketService.isShutdownRequested()) {
          setConnectionStatus('error');
        }
        // Reconnect is owned by websocketService (backoff + auth-failure policy).
      }
    );
  }, []);

  /**
   * Disconnect WebSocket connection
   * Should only be called on logout
   */
  const disconnect = useCallback(() => {
    websocketService.disconnect();
    setIsConnected(false);
    setConnectionStatus('disconnected');
  }, []);

  // Initialize connection when component mounts (if user already has a token, e.g. refresh)
  useEffect(() => {
    const token = sessionStorage.getItem('token') || sessionStorage.getItem('authToken');
    if (token) {
      connect();
    }
  }, [connect]);

  useEffect(() => {
    const onAuthFailure = () => {
      setIsConnected(false);
      setConnectionStatus('auth-failed');
    };
    window.addEventListener('websocket-auth-failure', onAuthFailure);
    return () => window.removeEventListener('websocket-auth-failure', onAuthFailure);
  }, []);

  // Connect as soon as login completes (token was absent on first paint — mount effect skipped)
  useEffect(() => {
    const handleLogin = () => {
      const token = sessionStorage.getItem('token') || sessionStorage.getItem('authToken');
      if (token) {
        connect();
      }
    };

    window.addEventListener('user-logged-in', handleLogin);
    return () => window.removeEventListener('user-logged-in', handleLogin);
  }, [connect]);

  // Listen for logout events to disconnect
  useEffect(() => {
    const handleLogout = () => {
      disconnect();
    };

    // Listen for custom logout event
    window.addEventListener('user-logged-out', handleLogout);

    return () => {
      window.removeEventListener('user-logged-out', handleLogout);
    };
  }, [disconnect]);

  const value = {
    isConnected,
    connectionStatus,
    connect,
    disconnect,
  };

  return (
    <WebSocketContext.Provider value={value}>
      <SessionConflictListener />
      <QueueRealtimeSyncListener />
      {children}
    </WebSocketContext.Provider>
  );
};

/**
 * Hook to use WebSocket context
 */
export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (context === undefined) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};
